import type { ReactNode } from "react";
import { ButtonGroup, Typography, useMediaQuery } from "@mui/material";
import type { TypographyProps } from "@mui/material";
import type Statut from "constants/Statut";
import { useDebounce } from "use-debounce";
import StatutButton from "./StatutButton";
import type { StatutAssociation } from "constants/SatutMap";

interface StatutButtonGroupProps extends TypographyProps {
  statutOptions: Array<StatutAssociation>;
  onChangeStatut: (newStatut: Statut | undefined) => void;
  value: Statut | undefined;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  smallLabels?: boolean;
}

function StatutButtonGroup({
  statutOptions,
  onChangeStatut,
  value,
  disabled = false,
  size = "medium",
  smallLabels = false,
  ...typographyProps
}: Readonly<StatutButtonGroupProps>): ReactNode {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  // on debounce isSmallScreen pour une question de performance, trop de rerenders inutiles sinon
  const [debouncedIsSmallScreen] = useDebounce(isSmallScreen, 500);

  return (
    <ButtonGroup variant="outlined" size={debouncedIsSmallScreen ? "small" : size}>
      {statutOptions.map((statutOption) => (
        <StatutButton
          key={statutOption.statut}
          color={statutOption.color}
          onClick={() => {
            if (value === statutOption.statut) {
              onChangeStatut(undefined);
            } else {
              onChangeStatut(statutOption.statut);
            }
          }}
          checked={value === statutOption.statut}
          label={
            <Typography {...typographyProps}>
              {debouncedIsSmallScreen || smallLabels ? statutOption.smallLabel : statutOption.label}
            </Typography>
          }
          disabled={disabled}
        />
      ))}
    </ButtonGroup>
  );
}

export default StatutButtonGroup;
